import Common from '@/libs/common';

/**
 * Under this displacement a leg is mostly GPS noise: the driver phones report an
 * accuracy around 10 m. Polled every 5 s, a vehicle at 30 km/h covers 40 m: a
 * moving one crosses the threshold on a single poll, a crawling one over a few.
 */
const MIN_DISPLACEMENT_METERS = 20;

type LatLng = number[];

interface Track {
  anchor: LatLng;
  heading?: number;
}

/** Compass heading from one position to another, in degrees clockwise from North (MapLibre `icon-rotate`). */
export function headingBetween(from: LatLng, to: LatLng): number {
  const lonFact = Math.cos((((from[0] + to[0]) / 2) * Math.PI) / 180);
  const north = to[0] - from[0];
  const east = (to[1] - from[1]) * lonFact;
  return ((Math.atan2(east, north) * 180) / Math.PI + 360) % 360;
}

/**
 * Where each vehicle is actually going, deduced from the positions it reports, as
 * Display does. The API `bearing` of an on-demand vehicle is the phone's own, stale
 * as soon as the bus stops, and there is no trip or shape to fall back on.
 */
export function createHeadingTracker() {
  const tracks = new Map<string, Track>();

  return {
    /**
     * The heading, or undefined until the vehicle has covered enough ground for it
     * to mean anything. The anchor only moves once the threshold is crossed, so slow
     * progress adds up, and a standing vehicle keeps the heading it arrived with.
     */
    update(deviceId: string, position: LatLng): number | undefined {
      const track = tracks.get(deviceId);
      if (!track) {
        tracks.set(deviceId, { anchor: position });
        return undefined;
      }
      const covered = Common.coordDistance(
        { latitude: track.anchor[0], longitude: track.anchor[1] },
        { latitude: position[0], longitude: position[1] },
      );
      if (covered >= MIN_DISPLACEMENT_METERS) {
        track.heading = headingBetween(track.anchor, position);
        track.anchor = position;
      }
      return track.heading;
    },

    /** Forget the vehicles that left the feed. */
    prune(activeDeviceIds: Set<string>) {
      tracks.forEach((_, deviceId) => {
        if (!activeDeviceIds.has(deviceId)) tracks.delete(deviceId);
      });
    },
  };
}
